import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { useMediaQuery, Theme, Box } from "@mui/material";
import { SymbolV2 } from "@bn/api/apiv1";
import { useMandatorContext } from "@bn/theming";
import { goToDashboard } from "@helpers/mainAppUrls";
import { useSecurityLink } from "@hooks";
import { useAppLayout } from "@providers/AppLayoutProvider";
import { usePremiumFeaturesModal } from "@components/PremiumFeaturesModal";
import { UserMenu } from "@components/common/layouts/AppSidebar/UserMenu.tsx";
import { ArivaPremiumButton } from "./ArivaPremiumButton";
import { FakeSearchField } from "./FakeSearchField";
import {
  AppSidebarHeader as HeaderOrig,
  AppSidebarHeaderSmall,
} from "./AppSidebarHeader";
import { Sidebar, SidebarResponsive } from "./Sidebar";
import { AppSidebarMenu } from "./AppSidebarMenu";

const Header = styled(HeaderOrig)<{ collapsed?: boolean }>`
  ${({ collapsed, theme }) =>
    !collapsed &&
    css`
      margin-right: ${theme.spacing(8)};
    `}
`;

const HeaderSmallStyled = styled(AppSidebarHeaderSmall)`
  flex-grow: 1;
`;

const FakeInputWrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isSmall" && prop !== "matches" && prop !== "collapsed",
})<{
  isSmall: boolean;
  matches?: boolean;
  collapsed?: boolean;
}>`
  margin-bottom: ${({ theme, matches }) => theme.spacing(matches ? 2 : 4)};
  ${({ isSmall }) =>
    isSmall &&
    css`
      display: flex;
      align-items: center;
    `}
`;

export const AppSidebar = () => {
  const matches = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const { sidebarCollapsed: collapsed, toggleSidebarCollapsed } =
    useAppLayout();
  const getSecurityHref = useSecurityLink();
  const { openPremiumModal, premiumModal } = usePremiumFeaturesModal();
  const { isAriva } = useMandatorContext();

  const isSymbolClickable = (symbol: SymbolV2) =>
    Boolean(getSecurityHref(symbol));

  if (matches) {
    return (
      <>
        <SidebarResponsive
          collapsed={collapsed}
          onMenuOpen={toggleSidebarCollapsed}
          header={
            <HeaderSmallStyled
              onTestPremiumClick={openPremiumModal}
              onLogoClick={goToDashboard}
            />
          }
        >
          {!isAriva && (
            <FakeInputWrapper isSmall={collapsed && !matches} matches={matches}>
              <FakeSearchField
                collapsed={collapsed}
                getSymbolHref={getSecurityHref}
                isSymbolClickable={isSymbolClickable}
                sideBar
              />
            </FakeInputWrapper>
          )}

          <AppSidebarMenu collapsed={collapsed} />
          {isAriva && <ArivaPremiumButton onClick={openPremiumModal} />}
        </SidebarResponsive>
        {premiumModal}
      </>
    );
  }

  return (
    <>
      <Sidebar
        collapsed={collapsed}
        onCollapseClick={toggleSidebarCollapsed}
        header={
          <Header
            onTestPremiumClick={openPremiumModal}
            onLogoClick={goToDashboard}
          />
        }
      >
        {!isAriva && (
          <FakeInputWrapper
            isSmall={collapsed && !matches}
            collapsed={collapsed}
          >
            <FakeSearchField
              collapsed={collapsed}
              getSymbolHref={getSecurityHref}
              isSymbolClickable={isSymbolClickable}
              sideBar
            />
          </FakeInputWrapper>
        )}
        <Box flex={1} display="flex" flexDirection="column" gap={2} mb={4}>
          <AppSidebarMenu collapsed={collapsed} />
        </Box>
        {isAriva && <ArivaPremiumButton onClick={openPremiumModal} />}
        <UserMenu />
      </Sidebar>
      {premiumModal}
    </>
  );
};
